import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useDevices,
  useCreateDevice,
  useUpdateDevice,
  useUnassignDevice,
} from "@/api/queries/devices";
import { useUsers } from "@/api/queries/users";
import { formatDateTime } from "@/lib/format";
import { useCurrentUser } from "@/api/queries/me";
import { useAppStore } from "@/store/app-store";
import { useAuth } from "@/auth/AuthProvider";
import { isRespaiAdmin } from "@/lib/permissions";

// Device registration and key rotation are platform-operations concerns, so the
// page is restricted to respai_admin — chiefs don't manage the sensor fleet.
export function DevicesPage() {
  const { identity } = useAuth();
  if (!isRespaiAdmin(identity?.role)) {
    return (
      <p className="text-sm text-destructive">
        Device management is restricted to RespAI platform administrators.
      </p>
    );
  }
  return <DevicesPageInner />;
}

function DevicesPageInner() {
  const me = useCurrentUser();
  const tz = me.data?.department?.timezone;
  const selectedDeptId = useAppStore((s) => s.selectedDepartmentId);
  const showDepartmentColumn = selectedDeptId === "all";
  const { data: devices, isLoading, isError } = useDevices();
  const { data: users } = useUsers();
  const createDevice = useCreateDevice();

  const [showRegister, setShowRegister] = useState(false);
  const [serial, setSerial] = useState("");
  const [model, setModel] = useState("BW Ultra");

  const [assigningDeviceId, setAssigningDeviceId] = useState<string | null>(
    null,
  );
  const [selectedUserId, setSelectedUserId] = useState("");

  const firefighters = (users ?? []).filter(
    (u) => u.role === "firefighter" || u.role === "incident_commander",
  );

  function handleRegister() {
    if (!serial.trim()) return;
    createDevice.mutate(
      { serial_number: serial.trim(), model },
      {
        onSuccess: () => {
          setSerial("");
          setModel("BW Ultra");
          setShowRegister(false);
        },
      },
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Devices</h1>
          <p className="text-sm text-muted-foreground">
            Registered gas detectors and sensor assignments.
          </p>
        </div>
        <Button onClick={() => setShowRegister((v) => !v)}>
          {showRegister ? "Cancel" : "Register device"}
        </Button>
      </header>

      {showRegister && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Register a new device</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm">
                Serial number
                <Input
                  value={serial}
                  onChange={(e) => setSerial(e.target.value)}
                  placeholder="e.g. 5220ULT011184900061"
                  className="mt-1 w-64"
                />
              </label>
              <label className="text-sm">
                Model
                <Input
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="mt-1 w-40"
                />
              </label>
              <Button
                onClick={handleRegister}
                disabled={!serial.trim() || createDevice.isPending}
              >
                {createDevice.isPending ? "Registering…" : "Register"}
              </Button>
            </div>
            {createDevice.isError && (
              <p className="text-sm text-destructive">
                {(createDevice.error as Error).message}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {devices?.length ?? 0} device{(devices?.length ?? 0) !== 1 && "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-6" />
              <Skeleton className="h-6" />
            </div>
          ) : isError ? (
            <p className="p-6 text-sm text-destructive">
              Failed to load devices. Check your connection and try again.
            </p>
          ) : devices && devices.length > 0 ? (
            <Table>
              <Thead>
                <Tr>
                  <Th>Serial</Th>
                  {showDepartmentColumn && <Th>Department</Th>}
                  <Th>Model</Th>
                  <Th>Status</Th>
                  <Th>Assigned to</Th>
                  <Th>Last seen</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {devices.map((d) => (
                  <Tr key={d.id}>
                    <Td className="font-mono text-sm">{d.serial_number}</Td>
                    {showDepartmentColumn && (
                      <Td className="text-sm">
                        {d.department_name ?? (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </Td>
                    )}
                    <Td>
                      {d.vendor} {d.model}
                    </Td>
                    <Td>
                      <Badge
                        variant={
                          d.status === "active" ? "success" : "secondary"
                        }
                      >
                        {d.status}
                      </Badge>
                    </Td>
                    <Td>
                      {d.assigned_user_name ?? (
                        <span className="text-muted-foreground">
                          Unassigned
                        </span>
                      )}
                    </Td>
                    <Td className="text-sm">
                      {d.last_seen_at
                        ? formatDateTime(d.last_seen_at, tz)
                        : "Never"}
                    </Td>
                    <Td>
                      {assigningDeviceId === d.id ? (
                        <AssignForm
                          deviceId={d.id}
                          firefighters={firefighters}
                          selectedUserId={selectedUserId}
                          onSelectUser={setSelectedUserId}
                          onCancel={() => {
                            setAssigningDeviceId(null);
                            setSelectedUserId("");
                          }}
                          onDone={() => {
                            setAssigningDeviceId(null);
                            setSelectedUserId("");
                          }}
                        />
                      ) : (
                        <div className="flex flex-col gap-2">
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setAssigningDeviceId(d.id);
                                setSelectedUserId(
                                  d.assigned_user_id ?? "",
                                );
                              }}
                            >
                              {d.assigned_user_id ? "Reassign" : "Assign"}
                            </Button>
                            {d.assigned_user_id && (
                              <UnassignButton deviceId={d.id} />
                            )}
                          </div>
                        </div>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">
              No devices registered yet. Click "Register device" to add one.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AssignForm({
  deviceId,
  firefighters,
  selectedUserId,
  onSelectUser,
  onCancel,
  onDone,
}: {
  deviceId: string;
  firefighters: { id: string; display_name: string; email: string }[];
  selectedUserId: string;
  onSelectUser: (id: string) => void;
  onCancel: () => void;
  onDone: () => void;
}) {
  const update = useUpdateDevice(deviceId);
  return (
    <div className="flex items-center gap-2">
      <select
        className="rounded border border-border bg-background px-2 py-1 text-sm"
        value={selectedUserId}
        onChange={(e) => onSelectUser(e.target.value)}
      >
        <option value="">Select person…</option>
        {firefighters.map((u) => (
          <option key={u.id} value={u.id}>
            {u.display_name || u.email}
          </option>
        ))}
      </select>
      <Button
        size="sm"
        disabled={!selectedUserId || update.isPending}
        onClick={() =>
          update.mutate(
            { assigned_user_id: selectedUserId },
            { onSuccess: onDone },
          )
        }
      >
        {update.isPending ? "…" : "Save"}
      </Button>
      <Button variant="ghost" size="sm" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

function UnassignButton({ deviceId }: { deviceId: string }) {
  const unassign = useUnassignDevice(deviceId);
  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={unassign.isPending}
      onClick={() => unassign.mutate()}
    >
      {unassign.isPending ? "…" : "Unassign"}
    </Button>
  );
}

